import { ActionTree, ActionContext } from 'vuex';
import { RootState } from '@/store';
import { State } from './state';
import { Mutations } from './mutations';
import { OpportunitiesMutationTypes } from './mutations';
import {
  GetOpportunitiesPayload,
  SetOpportunityFieldValuePayload,
  GetCalculatedOpportunitiesPayload,
  OpportunityResponse,
  GetExistingOpportunitiesResponse,
  GetSaveOpportunitiesDataItem,
  SubmitTaskOpportunitiesPayload,
  PotentialOpportunitiesResponse,
  SaveOpportunityResponse,
} from './types';

import ServerAPI, {
  combineQueryParams,
  handleError,
} from '@/services/serverAPI';
import { arrayToKeyValue } from '@/helpers/opportunities';
import { TaskApiResponse, TaskApiResponseResult } from '../taskView/types';
import { TaskStatus } from '@/enums/Tasks';
import { createTasks } from '@/helpers/tasks';

type AugmentedAction = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export enum OpportunitiesActionTypes {
  GET_ACTION_TASKS = 'GET_ACTION_TASKS',
  SET_TASK_IN_PROGRESS = 'SET_TASK_IN_PROGRESS',
  GET_OPPORTUNITIES = 'GET_OPPORTUNITIES',
  SET_OPPORTUNITY_FIELD_VALUE = 'SET_OPPORTUNITY_FIELD_VALUE',
  GET_CALCULATED_OPPORTUNITIES = 'GET_CALCULATED_OPPORTUNITIES',
  SAVE_OPPORTUNITY = 'SAVE_OPPORTUNITY',
  SUBMIT_TASK_OPPORTUNITIES = 'SUBMIT_TASK_OPPORTUNITIES',
  GET_POTENTIAL_OPPORTUNITIES = 'GET_POTENTIAL_OPPORTUNITIES',
  ADD_OPPORTUNITY = 'ADD_OPPORTUNITY',
  RESET_OPPORTUNITIES_STATE = 'RESET_OPPORTUNITIES_STATE',
}

export interface Actions {
  [OpportunitiesActionTypes.GET_ACTION_TASKS](
    { commit }: AugmentedAction,
    clientId: string
  ): Promise<void>;
  [OpportunitiesActionTypes.SET_TASK_IN_PROGRESS](
    { commit }: AugmentedAction,
    payload: string
  ): Promise<void>;
  [OpportunitiesActionTypes.GET_OPPORTUNITIES](
    { commit }: AugmentedAction,
    payload: GetOpportunitiesPayload
  ): Promise<void>;
  [OpportunitiesActionTypes.SET_OPPORTUNITY_FIELD_VALUE](
    { commit }: AugmentedAction,
    payload: SetOpportunityFieldValuePayload
  ): Promise<void>;
  [OpportunitiesActionTypes.GET_CALCULATED_OPPORTUNITIES](
    { commit }: AugmentedAction,
    payload: GetCalculatedOpportunitiesPayload
  ): Promise<void>;
  [OpportunitiesActionTypes.SAVE_OPPORTUNITY](
    { commit }: AugmentedAction,
    payload: {
      taskId: string;
      opportunity: GetSaveOpportunitiesDataItem;
    }
  ): Promise<void>;
  [OpportunitiesActionTypes.SUBMIT_TASK_OPPORTUNITIES](
    { commit }: AugmentedAction,
    payload: SubmitTaskOpportunitiesPayload
  ): Promise<void>;
  [OpportunitiesActionTypes.GET_POTENTIAL_OPPORTUNITIES](
    { commit }: AugmentedAction,
    payload: { clientId: string }
  ): Promise<void>;
  [OpportunitiesActionTypes.ADD_OPPORTUNITY](
    { commit }: AugmentedAction,
    payload: {
      opportunity: OpportunityResponse;
      clientId: string;
    }
  ): Promise<void>;
  [OpportunitiesActionTypes.RESET_OPPORTUNITIES_STATE]({
    commit,
  }: AugmentedAction): Promise<void>;
}

export const actions: ActionTree<State, RootState> & Actions = {
  async [OpportunitiesActionTypes.GET_ACTION_TASKS]({ commit }, clientId) {
    commit(OpportunitiesMutationTypes.SET_ACTION_TASKS_LOADING, true);

    try {
      const paramString = combineQueryParams({
        ['filters']: [
          {
            key: 'task_status',
            value: `${TaskStatus.TODO},${TaskStatus.NOT_STARTED},${TaskStatus.IN_PROGRESS}`,
          },
        ],
        ['sort_by']: `dateCreated`,
        ['sort_descending']: true,
        ['size']: 999999,
        ['start']: 0,
      });

      const tasksRes: TaskApiResponse = await ServerAPI.get(
        `/clients-new/${clientId}/tasks${paramString}`
      );

      const tasks = createTasks(tasksRes.results);

      commit(OpportunitiesMutationTypes.SET_ACTION_TASKS, tasks);
    } catch (e) {
      handleError(e);
    } finally {
      commit(OpportunitiesMutationTypes.SET_ACTION_TASKS_LOADING, false);
    }
  },
  async [OpportunitiesActionTypes.SET_TASK_IN_PROGRESS]({ commit }, payload) {
    commit(OpportunitiesMutationTypes.SET_TASK_IN_PROGRESS, payload);
  },
  async [OpportunitiesActionTypes.GET_OPPORTUNITIES]({ commit }, payload) {
    try {
      commit(OpportunitiesMutationTypes.SET_IS_LOADING, true);

      const opportunities: GetExistingOpportunitiesResponse = await ServerAPI.get(
        `/opportunities?filters=task_id:${payload.taskId}`
      );

      commit(OpportunitiesMutationTypes.SET_OPPORTUNITIES, {
        taskId: payload.taskId,
        opportunities: opportunities[0],
      });
    } catch (e) {
      handleError(e);
    } finally {
      commit(OpportunitiesMutationTypes.SET_IS_LOADING, false);
    }
  },
  async [OpportunitiesActionTypes.SET_OPPORTUNITY_FIELD_VALUE](
    { commit },
    payload
  ) {
    try {
      if (
        payload &&
        payload.opportunityName !== '' &&
        payload.fieldName !== '' &&
        payload.taskId !== ''
      ) {
        // Must have Opportunity & Field Name
        commit(OpportunitiesMutationTypes.SET_OPPORTUNITY_FIELD_VALUE, payload);
      } else {
        // eslint-disable-next-line no-console
        console.error(
          'Can not set opportunity field value as some details are missing'
        );
      }
    } catch (e) {
      handleError(e);
    }
  },
  async [OpportunitiesActionTypes.GET_CALCULATED_OPPORTUNITIES](
    { commit },
    payload
  ) {
    try {
      const calculatedResponse: OpportunityResponse = await ServerAPI.post(
        `/opportunities/calculate`,
        payload
      );

      if (calculatedResponse) {
        commit(
          OpportunitiesMutationTypes.SET_OPPORTUNITY_FIELDS_AFTER_CALCULATION,
          {
            taskId: payload.taskId,
            opportunity: calculatedResponse,
          }
        );
      }
    } catch (e) {
      handleError(e);
    }
  },
  async [OpportunitiesActionTypes.SAVE_OPPORTUNITY]({ commit }, payload) {
    try {
      const saveRes: SaveOpportunityResponse[] = await ServerAPI.patch(
        `/opportunities`,
        [payload.opportunity]
      );

      if (saveRes && saveRes[0]) {
        commit(OpportunitiesMutationTypes.SET_VALIDATION, {
          taskId: payload.taskId,
          opportunityRes: saveRes[0],
        });
      }
    } catch (e) {
      handleError(e);
    }
  },
  async [OpportunitiesActionTypes.SUBMIT_TASK_OPPORTUNITIES](_, payload) {
    try {
      await ServerAPI.patch(
        `/tasks/${payload.taskId}/submit`,
        payload.opportunities
      );
    } catch (e) {
      handleError(e);
    }
  },
  async [OpportunitiesActionTypes.GET_POTENTIAL_OPPORTUNITIES](
    { commit },
    payload
  ) {
    commit(OpportunitiesMutationTypes.SET_IS_MODAL_LOADING, true);

    try {
      const res: PotentialOpportunitiesResponse = await ServerAPI.get(
        `/clients-new/${payload.clientId}/opportunities`
      );

      commit(
        OpportunitiesMutationTypes.SET_POTENTIAL_OPPORTUNITIES,
        res.potentialOps
      );
    } catch (e) {
      handleError(e);
    } finally {
      commit(OpportunitiesMutationTypes.SET_IS_MODAL_LOADING, false);
    }
  },
  async [OpportunitiesActionTypes.ADD_OPPORTUNITY]({ commit }, payload) {
    commit(OpportunitiesMutationTypes.SET_IS_MODAL_SUBMITTING, true);

    try {
      const task: TaskApiResponseResult = await ServerAPI.post(
        `/clients-new/${payload.clientId}/tasks/new`
      );

      await ServerAPI.post(
        `/clients-new/${payload.clientId}/opportunities/new`,
        {
          taskId: task.taskId.dataValue,
          opportunityReference:
            payload.opportunity.opportunityReference.dataValue,
          identifiedData: arrayToKeyValue(payload.opportunity.fields),
        }
      );
      commit(
        OpportunitiesMutationTypes.SET_LAST_MADE_TASK,
        task.taskId.dataValue as string
      );
    } catch (e) {
      handleError(e);
    } finally {
      commit(OpportunitiesMutationTypes.SET_IS_MODAL_SUBMITTING, false);
    }
  },
  async [OpportunitiesActionTypes.RESET_OPPORTUNITIES_STATE]({ commit }) {
    commit(OpportunitiesMutationTypes.RESET_OPPORTUNITIES_STATE, undefined);
  },
};
